import { useState } from "react";
import { useTicketTypes, TicketType } from "@/hooks/useConfig";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function TicketTypeManagement() {
  const {
    ticketTypes,
    isLoading,
    createTicketType,
    updateTicketType,
    deleteTicketType,
  } = useTicketTypes();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const [name, setName] = useState("");
  const [editingTicketType, setEditingTicketType] = useState<TicketType | null>(null);
  const [deletingTicketType, setDeletingTicketType] = useState<TicketType | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || name.trim().length < 2) {
      toast.error("Name must be at least 2 characters");
      return;
    }

    createTicketType.mutate(
      { name: name.trim() },
      {
        onSuccess: () => {
          setName("");
          setIsCreateOpen(false);
        },
      }
    );
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingTicketType) return;

    if (!name || name.trim().length < 2) {
      toast.error("Name must be at least 2 characters");
      return;
    }

    updateTicketType.mutate(
      { id: editingTicketType.id, data: { name: name.trim() } },
      {
        onSuccess: () => {
          setName("");
          setEditingTicketType(null);
          setIsEditOpen(false);
        },
      }
    );
  };

  const handleDelete = async () => {
    if (!deletingTicketType) return;

    deleteTicketType.mutate(deletingTicketType.id, {
      onSuccess: () => {
        setDeletingTicketType(null);
        setIsDeleteOpen(false);
      },
    });
  };

  const openEditDialog = (ticketType: TicketType) => {
    setEditingTicketType(ticketType);
    setName(ticketType.name);
    setIsEditOpen(true);
  };

  const openDeleteDialog = (ticketType: TicketType) => {
    setDeletingTicketType(ticketType);
    setIsDeleteOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogTrigger asChild>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Ticket Type
          </Button>
        </DialogTrigger>
        <DialogContent>
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Add Ticket Type</DialogTitle>
              <DialogDescription>
                Create a new ticket type category
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Ticket Type Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Bug Report"
                  className="mt-1"
                  autoFocus
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setName("");
                  setIsCreateOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createTicketType.isPending}>
                {createTicketType.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <form onSubmit={handleEdit}>
            <DialogHeader>
              <DialogTitle>Edit Ticket Type</DialogTitle>
              <DialogDescription>
                Update the ticket type name
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-name">Ticket Type Name</Label>
                <Input
                  id="edit-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1"
                  autoFocus
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setName("");
                  setEditingTicketType(null);
                  setIsEditOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateTicketType.isPending}>
                {updateTicketType.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Update
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete "{deletingTicketType?.name}". This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setDeletingTicketType(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteTicketType.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[120px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ticketTypes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                  No ticket types yet. Create one to get started.
                </TableCell>
              </TableRow>
            ) : (
              ticketTypes.map((ticketType) => (
                <TableRow key={ticketType.id}>
                  <TableCell className="font-medium">{ticketType.name}</TableCell>
                  <TableCell>
                    {ticketType.created_at
                      ? new Date(ticketType.created_at).toLocaleDateString()
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(ticketType)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteDialog(ticketType)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
